import { motion } from 'framer-motion';
import { Mail, MapPin } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm p-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h1>
          
          <div className="prose prose-blue max-w-none">
            <p className="text-gray-600 mb-6">
              Welcome to Soft Standard Inc. ("Company," "we," "us," or "our"). These Terms of Service ("Terms") govern your use of our website www.softstandardsinc.com and the services we provide. By accessing or using our website and services, you agree to comply with these Terms. If you do not agree, please do not use our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Use of Our Services</h2>
            <p className="text-gray-600 mb-2">By using our services, you agree to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4">
              <li>Provide accurate and complete information when required</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Use our services only for lawful purposes</li>
            </ul>

            <p className="text-gray-600 mb-2">You must not:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4">
              <li>Engage in fraudulent, harmful, or abusive behavior</li>
              <li>Attempt to interfere with our website, servers, or security measures</li>
              <li>Copy, modify, or distribute our content without permission</li>
            </ul>

            <p className="text-gray-600 mb-6">
              We reserve the right to suspend or terminate your access if you violate these Terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Services & Payments</h2>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2">A. Service Description</h3>
            <p className="text-gray-600 mb-4">
              Soft Standard Inc. provides digital marketing solutions, including AI automation, website development, advertising, and social media marketing.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mb-2">B. Pricing & Payments</h3>
            <ul className="list-disc list-inside text-gray-600 mb-4">
              <li>Prices for our services are listed on our website or provided in a custom quote.</li>
              <li>Payments must be made in full before service delivery unless otherwise agreed.</li>
              <li>We accept credit cards, PayPal, and bank transfers.</li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-2">C. Refund Policy</h3>
            <ul className="list-disc list-inside text-gray-600 mb-6">
              <li>Due to the nature of digital services, we do not offer refunds once work has commenced.</li>
              <li>Refund requests will be reviewed on a case-by-case basis.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Intellectual Property</h2>
            <ul className="list-disc list-inside text-gray-600 mb-6">
              <li>All content, branding, logos, and materials on our website and services are owned by Soft Standard Inc.</li>
              <li>You may not reproduce, distribute, or use our intellectual property without written permission.</li>
              <li>Any materials provided to you as part of our services remain our property unless otherwise stated.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Limitation of Liability</h2>
            <ul className="list-disc list-inside text-gray-600 mb-6">
              <li>We provide our services "as is" without warranties of any kind.</li>
              <li>We are not responsible for any business losses, data breaches, or indirect damages resulting from our services.</li>
              <li>Our total liability is limited to the amount you paid for our services in the last 3 months.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Responsibilities</h2>
            <p className="text-gray-600 mb-2">By using our services, you agree to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4">
              <li>Provide accurate business and contact information</li>
              <li>Cooperate with our team to ensure project success</li>
              <li>Ensure compliance with marketing laws when using our services</li>
            </ul>
            <p className="text-gray-600 mb-6">
              We are not responsible for any legal issues arising from misuse of our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Privacy & Data Protection</h2>
            <p className="text-gray-600 mb-6">
              Your use of our services is also governed by our <a href="/privacy" className="text-blue-600 hover:text-blue-700">Privacy Policy</a>, which explains how we collect and use your data.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Termination</h2>
            <p className="text-gray-600 mb-2">We may suspend or terminate your access to our services if:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4">
              <li>You violate these Terms</li>
              <li>You fail to make required payments</li>
              <li>We decide to discontinue any part of our services</li>
            </ul>
            <p className="text-gray-600 mb-6">
              You may terminate your agreement with us by ceasing to use our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Governing Law</h2>
            <p className="text-gray-600 mb-6">
              These Terms shall be governed by and interpreted in accordance with the laws of New York. Any disputes shall be resolved through negotiation or in the courts of New York.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to These Terms</h2>
            <p className="text-gray-600 mb-6">
              We reserve the right to update these Terms at any time. Changes will be posted on our website, and continued use of our services constitutes acceptance of the new Terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-600 mb-4">If you have any questions about these Terms, contact us at:</p>
            
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <span className="text-gray-700">contact@softstandards.net</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-blue-600" />
                <span className="text-gray-700">18820B 69th AVE APT 1B New York</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}